import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { STATUS_META, SUBTASK_STATUS_META } from '@/constants/projectMeta';

const DAY = 86400000;
const parse = (s) => new Date(`${s}T00:00:00`);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SubtaskDot = ({ sub, pct }) => {
  const meta = SUBTASK_STATUS_META[sub.status] || SUBTASK_STATUS_META.pending;
  const anchor = pct > 70 ? 'right-0' : pct < 15 ? 'left-0' : 'left-0 -translate-x-1/2';
  return (
    <span className="group absolute top-1/2 z-[5]" style={{ left: `${pct}%` }} data-testid={`timeline-subtask-dot-${sub.id}`}>
      <span className={`block w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 cursor-default transition-transform duration-150 group-hover:scale-150 ${sub.overdue ? 'border-red-500' : 'border-[hsl(var(--background))]'}`}
        style={{ background: meta.accent }} />
      <span className={`pointer-events-none absolute top-2 ${anchor} whitespace-nowrap rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] px-2 py-1 text-[10px] text-[hsl(var(--text-primary))] shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-20`}
        data-testid={`timeline-subtask-tip-${sub.id}`}>
        {sub.title} · due {sub.deadline} · {meta.label}{sub.overdue ? ' · overdue' : ''}{sub.assignee_name ? ` · ${sub.assignee_name}` : ''}
      </span>
    </span>
  );
};

const buildScale = (from, to) => {
  const start = parse(from);
  const end = parse(to);
  const first = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth() + 1, 0);
  const totalDays = Math.max(1, Math.round((last - first) / DAY) + 1);
  const months = [];
  let cursor = new Date(first);
  while (cursor <= last) {
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const days = Math.round((Math.min(monthEnd, last) - cursor) / DAY) + 1;
    months.push({ key: `${cursor.getFullYear()}-${cursor.getMonth()}`, label: `${MONTHS[cursor.getMonth()]} ${String(cursor.getFullYear()).slice(2)}`, days });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return { first, last, totalDays, months };
};

export const ProjectTimelineView = ({ data, loading, onOpenTask }) => {
  const [expanded, setExpanded] = useState({});

  const scale = useMemo(() => {
    if (!data?.range?.from) return null;
    return buildScale(data.range.from, data.range.to);
  }, [data]);

  if (loading) return <div className="py-16 text-center text-sm text-[hsl(var(--text-muted))]">Loading…</div>;
  if (!data || !(data.projects || []).length || !scale) {
    return (
      <div className="py-16 text-center text-sm text-[hsl(var(--text-muted))]" data-testid="timeline-empty">
        No projects to plot yet. Add a Project / Event and a deadline to a task.
      </div>
    );
  }

  const pos = (start, end) => {
    const s = Math.max(0, Math.round((parse(start) - scale.first) / DAY));
    const e = Math.max(s, Math.round((parse(end) - scale.first) / DAY));
    return { left: `${(s / scale.totalDays) * 100}%`, width: `${Math.max(1.2, ((e - s + 1) / scale.totalDays) * 100)}%`, minWidth: '8px' };
  };
  const todayPos = data.today && parse(data.today) >= scale.first && parse(data.today) <= scale.last
    ? `${(Math.round((parse(data.today) - scale.first) / DAY) / scale.totalDays) * 100}%`
    : null;
  const dayPos = (d) => ((Math.round((parse(d) - scale.first) / DAY) + 0.5) / scale.totalDays) * 100;

  return (
    <div className="overflow-x-auto" data-testid="project-timeline">
      <div className="min-w-[880px]">
        <div className="flex border-b border-[hsl(var(--border))] pb-1.5 mb-2">
          <div className="w-56 shrink-0 text-[11px] uppercase tracking-wider text-[hsl(var(--text-muted))]">Project / Event</div>
          <div className="flex-1 flex">
            {scale.months.map((m) => (
              <div key={m.key} style={{ width: `${(m.days / scale.totalDays) * 100}%` }}
                className="text-[11px] text-[hsl(var(--text-muted))] border-l border-[hsl(var(--border))] pl-1.5 truncate">
                {m.label}
              </div>
            ))}
          </div>
        </div>

        <div className="relative space-y-1.5">
          {todayPos && (
            <div className="absolute top-0 bottom-0 left-56 right-0 pointer-events-none z-10">
              <div className="absolute top-0 bottom-0 w-px bg-[#E63946]/70" style={{ left: todayPos }} data-testid="timeline-today-marker" />
            </div>
          )}

          {data.projects.map((p) => {
            const open = !!expanded[p.project];
            const bar = pos(p.start, p.end);
            return (
              <div key={p.project} data-testid={`timeline-project-${p.project}`}>
                <div className="flex items-center">
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [p.project]: !e[p.project] }))}
                    className="w-56 shrink-0 flex items-center gap-1.5 text-left pr-2"
                    data-testid={`timeline-toggle-${p.project}`}
                  >
                    {open ? <ChevronDown size={13} className="text-[hsl(var(--text-muted))]" /> : <ChevronRight size={13} className="text-[hsl(var(--text-muted))]" />}
                    <span className="text-xs font-medium text-[hsl(var(--text-primary))] truncate" title={p.project}>{p.project}</span>
                    <span className="text-[10px] text-[hsl(var(--text-muted))] shrink-0">{p.completed}/{p.total}</span>
                    {!!p.overdue && <AlertTriangle size={11} className="text-red-400 shrink-0" />}
                  </button>
                  <div className="flex-1 relative h-7 rounded-md bg-white/[0.03]">
                    <div
                      className="absolute top-1 h-5 rounded-md bg-gradient-to-r from-[#E63946] to-[#F97316] shadow-lg shadow-[#E63946]/20 flex items-center px-2 overflow-hidden"
                      style={bar}
                      title={`${p.project}: ${p.start} → ${p.end}`}
                      data-testid={`timeline-bar-${p.project}`}
                    >
                      <span className="text-[10px] text-white/90 whitespace-nowrap">{p.avg_progress}%</span>
                    </div>
                  </div>
                </div>

                {open && (
                  <div className="mt-1 space-y-1" data-testid={`timeline-tasks-${p.project}`}>
                    {p.tasks.map((t) => {
                      const meta = STATUS_META[t.status] || STATUS_META.pending;
                      const tb = pos(t.start, t.end);
                      return (
                        <div key={t.id} className="flex items-center">
                          <button onClick={() => onOpenTask?.(t.id)}
                            className="w-56 shrink-0 pl-6 pr-2 text-left text-[11px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] truncate"
                            title={`${t.title} · ${t.pic_name || '—'}`}
                            data-testid={`timeline-task-${t.id}`}>
                            {t.title}
                          </button>
                          <div className="flex-1 relative h-5">
                            <div className="absolute top-1 h-3 rounded" style={{ ...tb, background: meta.accent, opacity: t.status === 'completed' ? 0.55 : 1 }}
                              title={`${t.start} → ${t.end} · ${meta.label} · ${t.progress}%`} />
                            {(t.subtasks || []).map((s) => <SubtaskDot key={s.id} sub={s} pct={dayPos(s.deadline)} />)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-[hsl(var(--border))]">
          {Object.entries(STATUS_META).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-[10px] text-[hsl(var(--text-muted))]">
              <span className="w-2.5 h-2.5 rounded" style={{ background: v.accent }} /> {v.label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 text-[10px] text-[hsl(var(--text-muted))]">
            <span className="w-px h-3 bg-[#E63946]" /> Today
          </span>
          <span className="inline-flex items-center gap-1.5 text-[10px] text-[hsl(var(--text-muted))]" data-testid="timeline-legend-subtask">
            <span className="w-2.5 h-2.5 rounded-full border-2 border-[hsl(var(--text-muted))]" /> Subtask deadline (hover for details)
          </span>
        </div>
      </div>
    </div>
  );
};
